'use client';

import { Box, Container, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

// Deadline: Nov. 19, 2026, at 3:00 p.m. ET (EST = UTC-5)
const TARGET_DEADLINE_UTC = new Date('2026-11-19T15:00:00-05:00').getTime();

interface TimeBlockProps {
  value: number;
  label: string;
}

const TimeUnit = ({ value, label }: TimeBlockProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <Box
      sx={{
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        color: '#f8fafc',
        borderRadius: '6px',
        px: { xs: 0.8, sm: 1.2 },
        py: { xs: 0.4, sm: 0.5 },
        minWidth: { xs: 38, sm: 44 },
        textAlign: 'center',
        fontWeight: 800,
        fontFamily: 'monospace',
        fontSize: { xs: '1rem', sm: '1.15rem' },
        lineHeight: 1.1,
        border: '1px solid rgba(255, 255, 255, 0.14)',
        boxShadow:
          'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 2px 4px rgba(0, 0, 0, 0.3)',
      }}
    >
      {String(value).padStart(2, '0')}
    </Box>
    <Typography
      sx={{
        fontSize: '0.6rem',
        color: '#94a3b8',
        fontWeight: 700,
        mt: 0.4,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {label}
    </Typography>
  </Box>
);

export default function FireDeadlineCards() {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isExpired: boolean;
  } | null>(null);

  useEffect(() => {
    const calc = () => {
      const diff = TARGET_DEADLINE_UTC - Date.now();
      if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true };
      }
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / 1000 / 60) % 60),
        seconds: Math.floor((diff / 1000) % 60),
        isExpired: false,
      };
    };

    setTimeLeft(calc());
    const timer = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!timeLeft) return null;

  return (
    <Box
      component="section"
      sx={{
        position: 'sticky',
        top: 64,
        zIndex: 11,
        background:
          'linear-gradient(90deg, #181536 0%, #171d54 50%, #053f2c 100%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        py: { xs: 1.25, md: 1 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1.25, md: 3 },
          }}
        >
          {/* Section 1: Badge + Explanatory Text */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              textAlign: { xs: 'center', sm: 'left' },
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            {/* Main & Supporting Copy */}
            <Box>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  fontWeight: 600,
                  color: '#ffffff',
                  mr: 1,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: '#fa6f6f',
                    fontWeight: 800,
                  }}
                >
                  FIRE Is Shutting Down
                </Box>{' '}
                . Get Ready for IRIS.
              </Typography>
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: '0.78rem', sm: '0.85rem' },
                  color: '#94a3b8',
                  fontWeight: 400,
                  display: { xs: 'none', lg: 'inline' },
                }}
              >
                Prepare your information returns for the transition to IRIS.
              </Typography>
            </Box>
          </Box>

          {/* Section 2: Countdown Timer */}
          <Box sx={{ flexShrink: 0 }}>
            <Stack direction="row" spacing={0.4} alignItems="flex-start">
              <TimeUnit value={timeLeft.days} label="Days" />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#64748b',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  px: 0.1,
                }}
              >
                :
              </Typography>
              <TimeUnit value={timeLeft.hours} label="Hours" />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#64748b',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  px: 0.1,
                }}
              >
                :
              </Typography>
              <TimeUnit value={timeLeft.minutes} label="Mins" />
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#64748b',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  px: 0.1,
                }}
              >
                :
              </Typography>
              <TimeUnit value={timeLeft.seconds} label="Secs" />
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
